import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LuArrowLeft, LuChevronLeft, LuChevronRight, LuX } from 'react-icons/lu';
import useDarkTheme from '../../../utils/useDarkTheme';
import themeImageSource from '../../../utils/themeImageSource';
import {
  MIN_MODAL_ZOOM,
  MODAL_MOBILE_QUERY,
  clampModalZoom,
} from '../../../utils/modalImageZoom';
import './style.css';

const SWIPE_THRESHOLD = 56;
const SWIPE_AXIS_RATIO = 1.2;
const SWIPE_DRAG_THRESHOLD = 8;

const ComparisonSide = ({ fallbackLabel, image, isDark, onClick, side }) => (
  <button className={`comparison-slide__side comparison-slide__side--${side}`} type="button" onClick={onClick}>
    <span className="comparison-slide__label">{image.label || fallbackLabel}</span>
    <span className="comparison-slide__media">
      <img src={themeImageSource(image, 'preview', isDark)} alt={image.alt} loading="lazy" />
    </span>
  </button>
);

const ComparisonSlide = ({ gestureProps, isDark, item, sourceLabel, resultLabel, onImageClick }) => (
  <div className="comparison-slide" {...gestureProps}>
    <ComparisonSide image={item.source} fallbackLabel={sourceLabel} isDark={isDark} side="source" onClick={() => onImageClick?.('source')} />
    <span className="comparison-slide__divider" aria-hidden="true" />
    <ComparisonSide image={item.result} fallbackLabel={resultLabel} isDark={isDark} side="result" onClick={() => onImageClick?.('result')} />
  </div>
);

const ComparisonGallery = ({ data, projectId, useProject01Template = false }) => {
  const items = data?.items || [];
  const isProjectOne = useProject01Template;
  const isDark = useDarkTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [detailSide, setDetailSide] = useState(null);
  const [isFullLoaded, setIsFullLoaded] = useState(false);
  const [scale, setScale] = useState(MIN_MODAL_ZOOM);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [fittedSize, setFittedSize] = useState({
    width: 0,
    height: 0,
    fitScale: 1,
    naturalWidth: 0,
    naturalHeight: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const detailViewportRef = useRef(null);
  const detailImageRef = useRef(null);
  const dragRef = useRef(null);
  const didDragRef = useRef(false);
  const activePointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const scaleRef = useRef(MIN_MODAL_ZOOM);
  const positionRef = useRef({ x: 0, y: 0 });
  const slideSwipeRef = useRef(null);
  const suppressSlideClickRef = useRef(false);
  const activeItem = items[activeIndex];
  const detailImage = detailSide ? activeItem?.[detailSide] : null;
  const detailImageSource = themeImageSource(detailImage, 'full', isDark);

  const setZoom = useCallback((nextScale, nextPosition) => {
    scaleRef.current = nextScale;
    positionRef.current = nextPosition;
    setScale(nextScale);
    setPosition(nextPosition);
  }, []);

  const resetGesture = useCallback(() => {
    activePointersRef.current.clear();
    pinchRef.current = null;
    dragRef.current = null;
    didDragRef.current = false;
    setIsDragging(false);
  }, []);

  const showPrevious = useCallback(() => {
    setActiveIndex((index) => Math.max(0, index - 1));
  }, []);
  const showNext = useCallback(() => {
    setActiveIndex((index) => Math.min(items.length - 1, index + 1));
  }, [items.length]);

  const resetImageView = useCallback(() => {
    setIsFullLoaded(false);
    setZoom(MIN_MODAL_ZOOM, { x: 0, y: 0 });
    setFittedSize({
      width: 0,
      height: 0,
      fitScale: 1,
      naturalWidth: 0,
      naturalHeight: 0,
    });
    resetGesture();
  }, [resetGesture, setZoom]);
  const resetDetail = useCallback(() => {
    setDetailSide(null);
    resetImageView();
  }, [resetImageView]);
  const closeModal = useCallback(() => {
    setIsOpen(false);
    resetDetail();
  }, [resetDetail]);
  const openDetail = useCallback((side) => {
    setDetailSide(side);
    resetImageView();
  }, [resetImageView]);

  const isMobileSwipe = useCallback(() =>
    isProjectOne && window.matchMedia(MODAL_MOBILE_QUERY).matches,
  [isProjectOne]);

  const handleSlidePointerDown = useCallback((event) => {
    if (!isMobileSwipe() || event.isPrimary === false) return;
    slideSwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    suppressSlideClickRef.current = false;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [isMobileSwipe]);

  const handleSlidePointerMove = useCallback((event) => {
    const gesture = slideSwipeRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (
      Math.abs(deltaX) > SWIPE_DRAG_THRESHOLD &&
      Math.abs(deltaX) > Math.abs(deltaY)
    ) {
      suppressSlideClickRef.current = true;
    }
  }, []);

  const finishSlideSwipe = useCallback((event) => {
    const gesture = slideSwipeRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (
      Math.abs(deltaX) >= SWIPE_THRESHOLD &&
      Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO
    ) {
      suppressSlideClickRef.current = true;
      if (deltaX > 0) showPrevious();
      else showNext();
    }
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    slideSwipeRef.current = null;
    window.setTimeout(() => {
      suppressSlideClickRef.current = false;
    }, 0);
  }, [showNext, showPrevious]);

  const cancelSlideSwipe = useCallback((event) => {
    if (slideSwipeRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    slideSwipeRef.current = null;
    suppressSlideClickRef.current = false;
  }, []);

  const handleSlideClickCapture = useCallback((event) => {
    if (!suppressSlideClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressSlideClickRef.current = false;
  }, []);

  const fitImageToViewport = useCallback((image = detailImageRef.current) => {
    const viewport = detailViewportRef.current;
    if (!viewport || !image?.naturalWidth || !image?.naturalHeight) return;
    const fitScale = Math.min(
      viewport.clientWidth / image.naturalWidth,
      viewport.clientHeight / image.naturalHeight
    );
    setFittedSize({
      width: image.naturalWidth * fitScale,
      height: image.naturalHeight * fitScale,
      fitScale,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
    });
    setZoom(MIN_MODAL_ZOOM, { x: 0, y: 0 });
  }, [setZoom]);

  const getPanBounds = useCallback((nextScale = scale) => {
    const viewport = detailViewportRef.current;
    if (!viewport || !fittedSize.width || !fittedSize.height || nextScale <= MIN_MODAL_ZOOM) return { x: 0, y: 0 };
    const overflowX = Math.max(0, fittedSize.width * nextScale - viewport.clientWidth);
    const overflowY = Math.max(0, fittedSize.height * nextScale - viewport.clientHeight);
    return { x: overflowX / 2, y: overflowY / 2 };
  }, [fittedSize.height, fittedSize.width, scale]);

  const clampPosition = useCallback((nextPosition, nextScale = scale) => {
    const bounds = getPanBounds(nextScale);
    return {
      x: Math.max(-bounds.x, Math.min(bounds.x, nextPosition.x)),
      y: Math.max(-bounds.y, Math.min(bounds.y, nextPosition.y)),
    };
  }, [getPanBounds, scale]);

  const changeScale = useCallback((nextScale) => {
    const normalizedScale = clampModalZoom(nextScale);
    const nextPosition = normalizedScale === MIN_MODAL_ZOOM
      ? { x: 0, y: 0 }
      : clampPosition(positionRef.current, normalizedScale);
    setZoom(normalizedScale, nextPosition);
  }, [clampPosition, setZoom]);

  const getPointerPair = () => Array.from(activePointersRef.current.values()).slice(0, 2);

  const getDistance = ([first, second]) =>
    Math.hypot(second.x - first.x, second.y - first.y);

  const getCenter = ([first, second]) => ({
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
  });

  const handlePointerDown = (event) => {
    event.stopPropagation();
    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);
    didDragRef.current = false;

    if (activePointersRef.current.size >= 2) {
      const pair = getPointerPair();
      pinchRef.current = {
        distance: getDistance(pair),
        center: getCenter(pair),
        zoom: scaleRef.current,
        position: positionRef.current,
      };
      dragRef.current = null;
      setIsDragging(true);
      return;
    }

    if (scaleRef.current > MIN_MODAL_ZOOM) {
      dragRef.current = {
        type: 'pan',
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startPosition: positionRef.current,
      };
      setIsDragging(true);
    } else if (isMobileSwipe()) {
      dragRef.current = {
        type: 'swipe',
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      };
    }
  };

  const handlePointerMove = (event) => {
    if (!activePointersRef.current.has(event.pointerId)) return;
    activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activePointersRef.current.size >= 2 && pinchRef.current) {
      const pair = getPointerPair();
      const currentDistance = getDistance(pair);
      const currentCenter = getCenter(pair);
      const pinch = pinchRef.current;
      if (!pinch.distance) return;

      const nextScale = clampModalZoom(pinch.zoom * (currentDistance / pinch.distance));
      const ratio = nextScale / pinch.zoom;
      const viewportRect = detailViewportRef.current?.getBoundingClientRect();
      if (!viewportRect) return;

      const viewportCenter = {
        x: viewportRect.left + viewportRect.width / 2,
        y: viewportRect.top + viewportRect.height / 2,
      };
      const startOffset = {
        x: pinch.center.x - viewportCenter.x,
        y: pinch.center.y - viewportCenter.y,
      };
      const currentOffset = {
        x: currentCenter.x - viewportCenter.x,
        y: currentCenter.y - viewportCenter.y,
      };
      const nextPosition = nextScale === MIN_MODAL_ZOOM
        ? { x: 0, y: 0 }
        : clampPosition({
          x: currentOffset.x - (startOffset.x - pinch.position.x) * ratio,
          y: currentOffset.y - (startOffset.y - pinch.position.y) * ratio,
        }, nextScale);

      didDragRef.current = true;
      setZoom(nextScale, nextPosition);
      return;
    }

    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (drag.type === 'swipe') {
      if (
        Math.abs(deltaX) > SWIPE_DRAG_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        didDragRef.current = true;
      }
      return;
    }
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) didDragRef.current = true;
    const nextPosition = clampPosition({
      x: drag.startPosition.x + deltaX,
      y: drag.startPosition.y + deltaY,
    });
    setZoom(scaleRef.current, nextPosition);
  };

  const finishDrag = (event) => {
    const gesture = dragRef.current;
    if (gesture?.type === 'swipe' && gesture.pointerId === event.pointerId) {
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      if (
        Math.abs(deltaX) >= SWIPE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO
      ) {
        const currentImageIndex = detailSide === 'result' ? 1 : 0;
        const direction = deltaX < 0 ? 1 : -1;
        const nextImageIndex = currentImageIndex + direction;

        if (nextImageIndex >= 0 && nextImageIndex <= 1) {
          openDetail(nextImageIndex === 0 ? 'source' : 'result');
        }
        didDragRef.current = true;
      }
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    activePointersRef.current.delete(event.pointerId);
    pinchRef.current = null;

    const remainingPointer = Array.from(activePointersRef.current.entries())[0];
    if (remainingPointer && scaleRef.current > MIN_MODAL_ZOOM) {
      const [pointerId, point] = remainingPointer;
      dragRef.current = {
        type: 'pan',
        pointerId,
        startX: point.x,
        startY: point.y,
        startPosition: positionRef.current,
      };
      setIsDragging(true);
    } else {
      dragRef.current = null;
      setIsDragging(false);
    }
  };

  const handleDetailImageClick = (event) => {
    event.stopPropagation();
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    changeScale(scaleRef.current === MIN_MODAL_ZOOM ? 1.7 : MIN_MODAL_ZOOM);
  };

  const handleDetailWheel = (event) => {
    event.preventDefault();
    changeScale(scaleRef.current + (event.deltaY < 0 ? 0.2 : -0.2));
  };

  useEffect(() => {
    resetImageView();
  }, [activeIndex, resetImageView]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.repeat) return;

      if (event.key === 'Escape') {
        if (detailSide) resetDetail();
        else closeModal();
        return;
      }
      if (detailSide) {
        if (event.key === 'ArrowRight' && detailSide === 'source') {
          event.preventDefault();
          openDetail('result');
          return;
        }
        if (event.key === 'ArrowLeft' && detailSide === 'result') {
          event.preventDefault();
          openDetail('source');
          return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          return;
        }
      }
      if (event.key === 'ArrowLeft') showPrevious();
      else if (event.key === 'ArrowRight') showNext();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModal, detailSide, isOpen, openDetail, resetDetail, showNext, showPrevious]);

  useEffect(() => {
    if (!detailSide) return undefined;
    const handleResize = () => {
      setZoom(MIN_MODAL_ZOOM, { x: 0, y: 0 });
      setFittedSize({
        width: 0,
        height: 0,
        fitScale: 1,
        naturalWidth: 0,
        naturalHeight: 0,
      });
      resetGesture();
      requestAnimationFrame(() => fitImageToViewport());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [detailSide, fitImageToViewport, resetGesture, setZoom]);

  useEffect(() => {
    if (!detailSide) return;
    resetImageView();
  }, [detailImageSource, detailSide, resetImageView]);

  if (!items.length) return null;

  const slideGestureProps = isProjectOne ? {
    onPointerDown: handleSlidePointerDown,
    onPointerMove: handleSlidePointerMove,
    onPointerUp: finishSlideSwipe,
    onPointerCancel: cancelSlideSwipe,
    onClickCapture: handleSlideClickCapture,
  } : undefined;

  const modal = (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className={`comparison-modal${isProjectOne ? ' comparison-modal--project-1' : ''}`}
          data-project-id={projectId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={data.modalLabel || data.title}
        >
          <button
            className="comparison-modal__backdrop"
            type="button"
            aria-label={detailSide
              ? (data.backLabel || 'Вернуться к сравнению')
              : (data.closeLabel || 'Закрыть')}
            onClick={detailSide ? resetDetail : closeModal}
          />
          <motion.div
            className={`comparison-modal__panel${detailImage ? ' is-detail' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <header className="comparison-modal__header">
              {detailImage ? (
                <button className="comparison-modal__back" type="button" onClick={resetDetail}>
                  <LuArrowLeft /> {data.backLabel || 'Вернуться к сравнению'}
                </button>
              ) : (
                <span className="comparison-modal__counter">{activeIndex + 1} / {items.length}</span>
              )}
              <button className="comparison-modal__close" type="button" aria-label={data.closeLabel || 'Закрыть'} onClick={closeModal}><LuX /></button>
            </header>

            {detailImage ? (
              <div
                className={`comparison-modal__detail${scale > MIN_MODAL_ZOOM ? ' is-zoomed' : ''}${isDragging ? ' is-dragging' : ''}`}
                ref={detailViewportRef}
                onClick={(event) => {
                  if (event.target === event.currentTarget) resetDetail();
                }}
                onWheel={handleDetailWheel}
              >
                {!isFullLoaded ? <span className="comparison-modal__loading">{data.loadingLabel || 'Загрузка изображения…'}</span> : null}
                <div
                  className="comparison-modal__image-stage"
                >
                  <img
                    className="comparison-modal__detail-image"
                    key={`${activeItem.id}-${detailSide}-${detailImageSource}`}
                    ref={detailImageRef}
                    src={detailImageSource}
                    alt={detailImage.alt}
                    width={fittedSize.naturalWidth || undefined}
                    height={fittedSize.naturalHeight || undefined}
                    decoding="async"
                    onLoad={(event) => {
                      fitImageToViewport(event.currentTarget);
                      setIsFullLoaded(true);
                    }}
                    onClick={handleDetailImageClick}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={finishDrag}
                    onPointerCancel={finishDrag}
                    style={{
                      opacity: isFullLoaded ? 1 : 0,
                      width: fittedSize.width ? fittedSize.width * scale : undefined,
                      height: fittedSize.height ? fittedSize.height * scale : undefined,
                      marginLeft: fittedSize.width ? -(fittedSize.width * scale) / 2 : undefined,
                      marginTop: fittedSize.height ? -(fittedSize.height * scale) / 2 : undefined,
                      transform: position.x || position.y
                        ? `translate3d(${position.x}px, ${position.y}px, 0)`
                        : 'none',
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="comparison-modal__body">
                <div className="comparison-modal__content">
                  <button className="comparison-modal__nav comparison-modal__nav--previous" type="button" aria-label={data.previousLabel || 'Предыдущее сравнение'} disabled={activeIndex === 0} onClick={showPrevious}>
                    <LuChevronLeft />
                    <span className="comparison-modal__nav-label">{data.previousLabel || 'Назад'}</span>
                  </button>
                  <ComparisonSlide gestureProps={slideGestureProps} isDark={isDark} item={activeItem} sourceLabel={data.sourceLabel} resultLabel={data.resultLabel} onImageClick={openDetail} />
                  <button className="comparison-modal__nav comparison-modal__nav--next" type="button" aria-label={data.nextLabel || 'Следующее сравнение'} disabled={activeIndex === items.length - 1} onClick={showNext}>
                    <span className="comparison-modal__nav-label">{data.nextLabel || 'Вперёд'}</span>
                    <LuChevronRight />
                  </button>
                </div>
                {activeItem.title || activeItem.description ? (
                  <div className="comparison-modal__copy">
                    {activeItem.title ? <strong>{activeItem.title}</strong> : null}
                    {activeItem.description ? <p>{activeItem.description}</p> : null}
                  </div>
                ) : null}
                {isProjectOne ? (
                  <div className="comparison-modal__mobile-navigation">
                    <button className="comparison-modal__nav" type="button" aria-label={data.previousLabel || 'Предыдущее сравнение'} disabled={activeIndex === 0} onClick={showPrevious}>
                      <LuChevronLeft />
                      <span className="comparison-modal__nav-label">{data.previousLabel || 'Назад'}</span>
                    </button>
                    <button className="comparison-modal__nav" type="button" aria-label={data.nextLabel || 'Следующее сравнение'} disabled={activeIndex === items.length - 1} onClick={showNext}>
                      <span className="comparison-modal__nav-label">{data.nextLabel || 'Вперёд'}</span>
                      <LuChevronRight />
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <>
      <section
        className={`project-final-model comparison-gallery${isProjectOne ? ' comparison-gallery--project-1' : ''}`}
        data-project-id={projectId}
      >
        <div className="comparison-gallery__heading">
          <div>
            <h2 className="project-final-model__title">{data.title}</h2>
            {data.description ? <p>{data.description}</p> : null}
          </div>
          <span className="comparison-gallery__counter">{activeIndex + 1} / {items.length}</span>
        </div>
        <ComparisonSlide gestureProps={slideGestureProps} isDark={isDark} item={activeItem} sourceLabel={data.sourceLabel} resultLabel={data.resultLabel} onImageClick={() => setIsOpen(true)} />
        <div className="comparison-gallery__footer">
          <div>
            {activeItem.title ? <strong>{activeItem.title}</strong> : null}
            {activeItem.description ? <p>{activeItem.description}</p> : null}
          </div>
          <div className="comparison-gallery__controls">
            <button type="button" aria-label={data.previousLabel || 'Предыдущее сравнение'} disabled={activeIndex === 0} onClick={showPrevious}><LuChevronLeft /></button>
            <button type="button" aria-label={data.nextLabel || 'Следующее сравнение'} disabled={activeIndex === items.length - 1} onClick={showNext}><LuChevronRight /></button>
          </div>
        </div>
      </section>
      {createPortal(modal, document.body)}
    </>
  );
};

export default ComparisonGallery;
